import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    Divider,
    Flex,
    Input,
    InputGroup,
    InputRightAddon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Select,
    SkeletonText,
    Spinner,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import CatalogueSelector from 'components/adspresso/CatalogueSelector';
import UpdateBudgetScheduleSpecs from 'components/adspresso/UpdateBudgetScheduleSpecs';
import currencySymbolMap from 'currency-symbol-map';
import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { GiStarShuriken } from "react-icons/gi";
import { useDispatch } from 'react-redux';
import { useCreateCampaignBudgetScheduleMutation, useDeleteCampaignBudgetSpecPeriodMutation, useGetcampaignByIdQuery, useGetCataloguesQuery, useUpdateCampaignMutation } from 'store/campaigns/campaignsApi';
import { setCampaignDataLoading } from 'store/campaigns/campaignsSlice';
import * as Yup from 'yup';
import { bidStrategiesForLeads, bidStrategiesForSales, budgetOptions } from '../../../../utils/campaignOptions';

function UpdateCampaignForm({selectedCampaign, onClose, adsAccountData, onSuccess}) {
    
    const background = useColorModeValue('gray.50', 'gray.800');
    const buttonTextColor = useColorModeValue('gray.800', '#fff');
    const toast = useToast();
    const dispatch = useDispatch();
    const currencyCode = adsAccountData?.currency;
    const currencySymbol = currencySymbolMap(adsAccountData?.currency);
    const { isOpen: isBidModalOpen, onOpen: onBidModalOpen, onClose: onBidModalClose } = useDisclosure();
    const [isSchedulingExpanded, setIsSchedulingExpanded] = useState(false);
    const [adsetBidAmounts, setAdsetBidAmounts] = useState({});
    const [deletingIndex, setDeletingIndex] = useState(null); 
    const [lastChangedPeriod, setLastChangedPeriod] = useState([]);

    const { data: campaignData, isLoading:isCampaignLoading, refetch:refetchCampaigns} = useGetcampaignByIdQuery(
        {
            campaignId: selectedCampaign?.campaign_id,
            adsAccountId: adsAccountData?.account_id
        },
        { skip: !selectedCampaign?.campaign_id }
    );

    const [updateCampaign, { isLoading: isCampaignUpdating }] = useUpdateCampaignMutation({});
    const [deleteCampaignBudgetSpecPeriod, { isLoading: isDeletingBudgetSpecPeriod }] = useDeleteCampaignBudgetSpecPeriodMutation({});
    const [createCampaignBudgetSchedule, { isLoading: isCampaignBudgetScheduleUpdating }] = useCreateCampaignBudgetScheduleMutation({});

    useEffect(() => {
        if (!campaignData) return;
      
        const campaign = campaignData[selectedCampaign?.campaign_id];
        if (!campaign) return;
      
        const {
          name,
          objective,
          bid_strategy,
          pacing_type,
          daily_budget,
          lifetime_budget,
          is_budget_schedule_enabled,
          budget_schedules,
          adsets,
          promoted_object
        } = campaign;
      
        formik.setValues({
          name,
          objective,
          isAdvantageCampaignEnabled: !!bid_strategy,
          isAdScheduled: pacing_type?.includes("day_parting") || false,
          accelerated: pacing_type?.includes("no_pacing") || false,
          budget: daily_budget ? 'daily_budget' : 'lifetime_budget',
          bid_strategy: bid_strategy || 'LOWEST_COST_WITHOUT_CAP',
          daily_budget: daily_budget ? Number(daily_budget) / 100 : 800,
          lifetime_budget: lifetime_budget ? Number(lifetime_budget) / 100 : 14000,
          is_budget_schedule_enabled: !!is_budget_schedule_enabled,
          budget_schedule_specs: [...(budget_schedules?.data || [])]
            .sort((a, b) => a.time_start - b.time_start)
            .map(spec => ({
                ...spec,
                budget_value: spec.budget_value_type === 'ABSOLUTE'
                ? spec.budget_value / 100
                : spec.budget_value
            })),
        isAdvantageCatalogueEnabled: (promoted_object &&  promoted_object?.product_catalog_id) ? true : false,
        product_catalog_id: (promoted_object && promoted_object?.product_catalog_id) ? promoted_object?.product_catalog_id : '',
        });
      
        const initialBids = {};
        adsets?.forEach(adSet => {
          initialBids[adSet?.id] = 5;
        });
        setAdsetBidAmounts(initialBids);
      
    }, [campaignData]);   
     
    const formik = useFormik({
        initialValues: {
            name: '',
            objective: '',
            isAdvantageCampaignEnabled: false,
            isAdScheduled: false,
            accelerated: false,
            isAdvantageCatalogueEnabled: false,
            product_catalog_id: '',
            budget: 'daily_budget',
            bid_strategy: 'LOWEST_COST_WITHOUT_CAP',
            daily_budget: '',
            lifetime_budget: '',
            is_budget_schedule_enabled: false,
            budget_schedule_specs: 
            [
                {
                  time_start: moment().add(1, 'day').startOf('day').unix(),
                  time_end: moment().add(2, 'day').startOf('day').unix(),
                  budget_value_type: 'ABSOLUTE',
                  budget_value: 5,
                },
            ],
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Campaign name is required'),
            // objective: Yup.string().required('Objective is required')  
        }),
        onSubmit: (values) => {
            const { name, budget, accelerated, daily_budget, lifetime_budget, bid_strategy, isAdvantageCampaignEnabled, isAdScheduled, is_budget_schedule_enabled } = values;

            // Common ads account payload
            const adsAccountPayload = {
                id: adsAccountData?.id,
                adsAccountId: adsAccountData?.account_id,
                adsAccountName: adsAccountData?.name,
                businessName: adsAccountData?.business?.name,
                businessId: adsAccountData?.business?.id
            };

            let payload = {
                name,
                adsAccount: adsAccountPayload
            };

            if (!isAdvantageCampaignEnabled) {
                // CASE 1: Not using Advantage Campaign
                const adset_budgets = [];

                if (Array.isArray(selectedCampaign?.adsets)) {
                    selectedCampaign.adsets.forEach((adset) => {
                        const adsetId = adset?.id;
                        const budgetValue = selectedCampaign?.daily_budget || selectedCampaign?.lifetime_budget;
                        const isDailyBudget = !!selectedCampaign?.daily_budget;

                        if (adsetId && budgetValue) {
                            adset_budgets.push({
                                adset_id: adsetId,
                                [isDailyBudget ? 'daily_budget' : 'lifetime_budget']: Number(budgetValue)
                            });
                        }

                    });
                }

                if (bid_strategy && selectedCampaign?.bid_strategy) {
                    payload.bid_strategy = bid_strategy;
                }

                if (adset_budgets.length > 0) {
                    payload.adset_budgets = adset_budgets;
                }

            } else {
                // CASE 2: Advantage Campaign Flow
                if (budget === 'lifetime_budget') {
                    payload.lifetime_budget = Number(lifetime_budget) * 100;
                    payload.currency = currencyCode;
                }

                if (budget === 'daily_budget') {
                    payload.daily_budget = Number(daily_budget) * 100;
                    payload.currency = currencyCode;
                }

                // Only add bid_strategy & adset_bid_amounts if it changed
                if (bid_strategy && bid_strategy !== selectedCampaign?.bid_strategy) {
                    payload.bid_strategy = bid_strategy;

                    // Only set adset_bid_amounts if bid_strategy is NOT "LOWEST_COST_WITHOUT_CAP"
                    if (bid_strategy !== "LOWEST_COST_WITHOUT_CAP") {
                        payload.adset_bid_amounts = {};

                        Object.entries(adsetBidAmounts).forEach(([adSetId, value]) => {
                            payload.adset_bid_amounts[adSetId] = value * 100;
                        });
                    }
                }

                if (accelerated) {
                    payload.pacing_type = ["no_pacing"];
                    if (isAdScheduled) {
                        payload.pacing_type.push("day_parting");
                    }
                } else {
                    payload.pacing_type = ["standard"];
                    if (isAdScheduled) {
                        payload.pacing_type.push("day_parting");
                    }
                }

                payload.is_budget_schedule_enabled = is_budget_schedule_enabled;
            }

        if (is_budget_schedule_enabled && lastChangedPeriod.length > 0) {
                Promise.all(
                    lastChangedPeriod.map((period) => {
                    const budgetPayload = {
                        time_start: period.time_start,
                        time_end: period.time_end,
                        budget_value_type: period.budget_value_type,
                        budget_value: period.budget_value_type === 'ABSOLUTE'
                        ? period.budget_value * 100
                        : period.budget_value,
                    };
                
                    return createCampaignBudgetSchedule({
                        payload: budgetPayload,
                        campaignId: selectedCampaign?.campaign_id,
                    }).unwrap();
                    })
                )
                .then(() => {

                })
                .catch((error) => {
                    toast({
                    position: "top-right",
                    title: "Error updating budget periods",
                    status: "error",
                    description: error?.data?.message,
                    duration: 4000,
                    isClosable: true,
                    });
                });
            }                  

            updateCampaign({
                campaignId: selectedCampaign?.campaign_id,
                payload
            }).unwrap()
                .then(() => {
                    refetchCampaigns();
                    onSuccess();
                    dispatch(setCampaignDataLoading(true));
                    // refetchCampaignsInsights();
                    toast({
                        position: "top-right",
                        title: "Campaign updated",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                    onClose();
                })
                .catch((error) => {
                    toast({
                        position: "top-right",
                        title: "Error updating campaign",
                        status: "error",
                        description: error?.data?.message,
                        duration: 4000,
                        isClosable: true,
                    });
                    console.error('Error updating campaign:', error);
                });
        }
    })

    const { data: dataCatalogues, error: errorCatalogues, isLoading: isLoadingCatalogues } = useGetCataloguesQuery(undefined, { skip: !formik?.values?.isAdvantageCatalogueEnabled });

    const handleRemovePeriod = (id) => {
        setDeletingIndex(id);

        deleteCampaignBudgetSpecPeriod({ periodId: id })
            .unwrap()
            .then(response => {
                refetchCampaigns();
            })
            .catch(error => {
                toast({
                    position: "top-right",
                    title: "Error removing budget period",
                    status: "error",
                    description: error?.data?.message,
                    duration: 4000,
                    isClosable: true,
                });
            });
    }

    const handleBudgetChange = (e) => {
        const selectedBudgetType = e.target.value;
        if (selectedBudgetType === 'daily_budget') {
            formik.setFieldValue('lifetime_budget', null);
            formik.setFieldValue('daily_budget', Number(formik.values.daily_budget));
        } else if (selectedBudgetType === 'lifetime_budget') {
            formik.setFieldValue('daily_budget', null);
            formik.setFieldValue('lifetime_budget', Number(formik.values.lifetime_budget));
        }
        formik.setFieldValue('budget', selectedBudgetType);
    };

    const isBudgetChanged = (() => {
        if (!selectedCampaign?.bid_strategy) return false;

        const originalDaily = Number(selectedCampaign?.daily_budget) / 100;
        const originalLifetime = Number(selectedCampaign?.lifetime_budget) / 100;

        return (
            (formik.values.budget === 'daily_budget' && Number(formik.values.daily_budget) !== originalDaily) ||
            (formik.values.budget === 'lifetime_budget' && Number(formik.values.lifetime_budget) !== originalLifetime) ||
            !selectedCampaign?.adsets?.length > 0
        );
    })();

  return (
    <Box>
        <Modal isOpen={isBidModalOpen} onClose={onBidModalClose} scrollBehavior="inside" isCentered size="2xl" closeOnOverlayClick={false} closeOnEsc={false}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader p={2} fontSize="md">Set bid caps for 3 ad sets</ModalHeader>
                <ModalCloseButton />
                <ModalBody borderTop="1px" borderColor="gray.100">
                    <Text fontSize="12px" mb={4}>
                        Set bid caps for all of the ad sets (published and inactive) in this campaign.
                    </Text>

                    <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                        <Table size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th fontSize="12px">Ad set name</Th>
                                    <Th fontSize="12px">Status</Th>
                                    <Th fontSize="12px">{formik.values.bid_strategy === 'LOWEST_COST_WITH_BID_CAP' ? 'Bid cap' : formik.values.bid_strategy === 'COST_CAP' ? 'Cost per result goal' : 'ROAS goal'}</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {campaignData && campaignData[selectedCampaign?.campaign_id]?.adsets?.map((adSet, id) => (
                                    <Tr key={id}>
                                        <Td fontSize="12px">{adSet.name}</Td>
                                        <Td fontSize="12px" borderRight={'2px'} borderRightColor={'green.500'}>{adSet.status}</Td>
                                        <Td fontSize="12px">
                                            <Input
                                                placeholder={currencySymbol}
                                                size="sm"
                                                width="80px"
                                                value={adsetBidAmounts[adSet?.id]}
                                                type="number"
                                                onChange={(e) => {
                                                    const newValue = parseFloat(e.target.value);
                                                    setAdsetBidAmounts(prev => ({
                                                        ...prev,
                                                        [adSet.id]: newValue,
                                                    }));
                                                }}
                                            />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>

                    <Box pt={8}>
                        <Flex gap={4}>
                            <Flex>
                                <Text fontWeight="bold">Campaign budget</Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="sm" p={'5px'}>
                                            <Text>
                                                {selectedCampaign?.daily_budget ? 'With a daily budget, we may spend more on some days and less on others, but will maintain an average daily budget.' : 'With a lifetime budget, we may spend more on some days and less on others, but will maintain an average lifetime budget.'}
                                            </Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" m={1} />
                                </Tooltip>
                            </Flex>
                            <Flex w={'120px'} direction='column' gap={1}>
                                {formik.values.budget === 'daily_budget' ? (
                                    <InputGroup>
                                        <Input
                                            name="daily_budget"
                                            placeholder={currencySymbol}
                                            bg="#fff"
                                            type="number"
                                            value={String(formik.values.daily_budget)}
                                            onChange={(e) => formik.setFieldValue('daily_budget', e.target.value)}
                                            onBlur={formik.handleBlur}
                                            borderRight={'none'}
                                            size={'sm'}
                                        />
                                        <InputRightAddon height={'32px'} fontSize={'10px'} bg='none' borderRadius={'none'}>
                                            {currencyCode}
                                        </InputRightAddon>
                                    </InputGroup>
                                ) : null}
                                {formik.values.budget === 'lifetime_budget' ? (
                                    <InputGroup>
                                        <Input
                                            name="lifetime_budget"
                                            placeholder={currencySymbol}
                                            bg="#fff"
                                            type="number"
                                            value={String(formik.values.lifetime_budget)}
                                            onChange={(e) => formik.setFieldValue('lifetime_budget', e.target.value)}
                                            onBlur={formik.handleBlur}
                                            borderRadius={'md'}
                                            size={'sm'}
                                        />
                                        <InputRightAddon height={'32px'} fontSize={'14px'}>
                                            {currencyCode}
                                        </InputRightAddon>
                                    </InputGroup>
                                ) : null}
                                <Flex>
                                    <Text fontSize={'12px'} color='gray' fontWeight='semibold'>{formik.values.budget === 'daily_budget' ? 'daily budget' : 'lifetime budget'}</Text>
                                </Flex>
                            </Flex>
                        </Flex>
                    </Box>
                </ModalBody>

                <ModalFooter>
                    <Flex borderTop="1px" borderColor="gray.100" pt={2} justifyContent="space-between" w="100%">
                        <Button onClick={onBidModalClose} size="sm">
                            Close
                        </Button>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                onBidModalClose();
                                onClose();
                            }}
                            size="sm"
                            colorScheme="brand"
                        >
                            Save
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>

       {isCampaignLoading ? 
            <Flex justify="center" align="center" height="70vh">
                <Spinner size="md" />
            </Flex>
        :  
            <>
                {campaignData ? 
                    <form onSubmit={formik.handleSubmit}>
                        <Box width="100%" maxWidth="600px" bg={background} p={4} borderRadius="md">
                            <Flex direction="column" gap={4}>
                                <Box>
                                    <Flex align="center">
                                        <Text mb={2}>
                                            Campaign Name
                                        </Text>
                                    </Flex>
                                    <Input
                                        placeholder="Enter campaign name"
                                        bg="#fff"
                                        name="name"
                                        size={'sm'}
                                        borderRadius="md"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.name && formik.errors.name && (
                                        <Text fontSize={'12px'} color="red.500" mt={1}>{formik.errors.name}</Text>
                                    )}
                                </Box>

                                <Box>
                                    <Flex align="center">
                                        <Text mb={2}>
                                            Objective
                                        </Text>
                                    </Flex>
                                    <Select
                                        name="type"
                                        borderRadius="md"
                                        value={formik.values.objective}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        size="sm"
                                        isDisabled
                                    >
                                        <option value="OUTCOME_LEAD">Leads</option>
                                        <option value="OUTCOME_SALES">Sales</option>
                                        <option value="OUTCOME_AWARENESS">Awareness</option>
                                        <option value="OUTCOME_APP_PROMOTION">App promotion</option>
                                        <option value="OUTCOME_ENGAGEMENT">Engagement</option>
                                        <option value="OUTCOME_TRAFFIC">Traffic</option>
                                    </Select>
                                </Box>

                                {/* Advantage Catalogue section */}
                                {formik?.values.objective === 'OUTCOME_SALES'  ?
                                    <>
                                        <Flex direction='column'>
                                            <Flex alignItems={'center'} justifyContent={'space-between'} mb={2}>
                                                <Box display={'flex'} alignItems={'center'} gap={1}>
                                                <Text fontWeight="bold">Advantage+ catalogue ads</Text>
                                                <GiStarShuriken />
                                                </Box>

                                                <Box display={'flex'} alignItems={'center'} gap={2}>
                                                <Text>{formik?.values.isAdvantageCatalogueEnabled ? 'On' : 'Off'}</Text>
                                                
                                                <Tooltip 
                                                    label="Your catalog can't be changed after your campaign has been created. Create a new campaign to use a different catalog." 
                                                    hasArrow 
                                                    p={2}
                                                    fontSize="sm"
                                                    borderRadius={5}
                                                    placement="right"
                                                >
                                                    <Switch
                                                        colorScheme='brand'
                                                        isDisabled
                                                        isChecked={formik?.values.isAdvantageCatalogueEnabled}
                                                        onChange={() =>
                                                            formik?.setFieldValue('isAdvantageCatalogueEnabled', !formik.values.isAdvantageCatalogueEnabled)
                                                        }
                                                    />
                                                </Tooltip>
                                                </Box>
                                            </Flex>

                                            <Text fontSize={'12px'} textAlign={'justify'}>
                                                Drive sales using your product information by showing relevant products to the right people.
                                            </Text>
                                        </Flex>

                                        {formik?.values?.isAdvantageCatalogueEnabled ? 
                                            <Box py={2}>
                                                {isLoadingCatalogues ? (
                                                    <Flex>
                                                        <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
                                                    </Flex>
                                                ) : dataCatalogues?.data?.length > 0 ? (
                                                    <Box>
                                                        <CatalogueSelector
                                                            prefilledId={formik.values.product_catalog_id}
                                                            dataCatalogues={dataCatalogues}
                                                            context={selectedCampaign?.adsets?.length > 0 ? 'update' : null}
                                                        />
                                                    </Box>
                                                ):null}
                                            </Box>
                                        :null}
                                    </>
                                :null}

                                {/* Advantage campaign section */}
                                {selectedCampaign?.smart_promotion_type === 'AUTOMATED_SHOPPING_ADS' ? null :
                                    <Box>
                                        <Flex alignItems={'center'} justifyContent={'space-between'} mb={2}>
                                            <Box display={'flex'} alignItems={'center'} gap={1}>
                                                <Text fontWeight="bold">Advantage campaign budget</Text>
                                                <GiStarShuriken />
                                            </Box>

                                            <Box display={'flex'} alignItems={'center'} gap={2}>
                                                <Text>{formik.values.isAdvantageCampaignEnabled ? 'On' : 'Off'}</Text>

                                                <Tooltip
                                                    borderRadius={5}
                                                    placement="right"
                                                    isDisabled={!!(selectedCampaign?.adsets?.length > 0)}
                                                    label={
                                                        <Box fontSize="sm" p="5px">
                                                            <Text>
                                                                To turn off Advantage campaign budget, you must create an ad set in this campaign.
                                                            </Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                    hasArrow
                                                >
                                                    <Box display="inline-block">
                                                        <Switch
                                                            colorScheme="brand"
                                                            isDisabled={!selectedCampaign?.adsets?.length}
                                                            isChecked={formik.values.isAdvantageCampaignEnabled}
                                                            onChange={() =>
                                                                formik.setFieldValue(
                                                                    'isAdvantageCampaignEnabled',
                                                                    !formik.values.isAdvantageCampaignEnabled
                                                                )
                                                            }
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        </Flex>
                                        <Text fontSize={'12px'} textAlign={'justify'}>Distribute your budget across ad sets to get more results. You can control spending for each ad set.</Text>
                                    </Box>
                                }

                                {formik.values.isAdvantageCampaignEnabled ? (
                                    <>
                                        <Flex my={2} direction={'column'} gap={2}>
                                            {/* Budget */}
                                            <Flex alignItems={'center'}>
                                                <Text fontWeight="bold">Budget</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="sm" p={'5px'}>
                                                            <Text >
                                                                If you choose a daily budget, you may spend more on some days and less on others but will maintain an average daily budget. If you choose a lifetime budget, we'll never spend more than that amount.
                                                            </Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                </Tooltip>
                                            </Flex>
                                            <Flex gap={1}>
                                                <Select
                                                    name="budget"
                                                    value={formik.values.budget}
                                                    onChange={handleBudgetChange}
                                                    onBlur={formik.handleBlur}
                                                    bg="#fff"
                                                    borderRadius={'md'}
                                                    maxW={'200px'}
                                                    size={'sm'}
                                                    isDisabled={selectedCampaign?.bid_strategy}
                                                >
                                                    {budgetOptions?.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Select>
                                                {formik.values.budget === 'daily_budget' ? (
                                                    <InputGroup>
                                                        <Input
                                                            name="daily_budget"
                                                            placeholder={currencySymbol}
                                                            bg="#fff"
                                                            type="number"
                                                            value={String(formik.values.daily_budget)}
                                                            onChange={(e) => formik.setFieldValue('daily_budget', e.target.value)}
                                                            onBlur={formik.handleBlur}
                                                            borderRadius={'md'}
                                                            size={'sm'}
                                                        />
                                                        <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                            {currencyCode}
                                                        </InputRightAddon>
                                                    </InputGroup>
                                                ) : null}
                                                {formik.values.budget === 'lifetime_budget' ? (
                                                    <InputGroup>
                                                        <Input
                                                            name="lifetime_budget"
                                                            placeholder={currencySymbol}
                                                            bg="#fff"
                                                            type="number"
                                                            value={String(formik.values.lifetime_budget)}
                                                            onChange={(e) => formik.setFieldValue('lifetime_budget', e.target.value)}
                                                            onBlur={formik.handleBlur}
                                                            borderRadius={'md'}
                                                            size={'sm'}
                                                        />
                                                        <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                            {currencyCode}
                                                        </InputRightAddon>
                                                    </InputGroup>
                                                ) : null}
                                            </Flex>
                                            <Box display={'flex'} flexDirection='column' gap='2'>
                                                <Flex alignItems={'center'}>
                                                    <Text fontWeight="bold">Campaign bid strategy</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p={'5px'}>
                                                                <Text >
                                                                    Cost per result goal, ROAS goal or bid caps allow you to enter a specific amount later for each ad set.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                    </Tooltip>
                                                </Flex>

                                                <Flex>
                                                    <Text fontSize={'12px'}>How we'll bid in ad auctions.</Text>
                                                </Flex>

                                                <Flex>
                                                    <Menu>
                                                        <Tooltip
                                                            label='You cannot switch the bid strategy because your delivery type is set to "accelerated", which is only available with the lowest cost with bid cap bid strategy. Please change your delivery type to standard to change your bid strategy.'
                                                            isDisabled={!(selectedCampaign?.pacing_type?.includes("no_pacing") ?? false)}
                                                            hasArrow
                                                            placement="top"
                                                            bg="gray.700"
                                                            color="white"
                                                            fontSize="12px"
                                                            p={3}
                                                            maxW="300px"
                                                        >
                                                        <MenuButton
                                                            as={Button}
                                                            borderRadius="md"
                                                            p={2}
                                                            bg={isBudgetChanged ? 'gray.50' : '#fff'}
                                                            cursor={isBudgetChanged ? "not-allowed" : "pointer"}
                                                            _hover={{ bg: isBudgetChanged ? 'gray.50' : '#fff' }}
                                                            w='auto'
                                                            fontSize='12px'
                                                        >
                                                            <Flex alignItems={'center'} justifyContent="space-between" gap='2'>
                                                            <Text color={isBudgetChanged  ? 'gray.500' : 'inherit'}>
                                                                {formik.values.bid_strategy
                                                                ? (formik.values.objective === 'OUTCOME_SALES'
                                                                    ? bidStrategiesForSales.find(option => option.value === formik.values.bid_strategy)?.label
                                                                    : formik.values.objective === 'OUTCOME_LEADS'
                                                                    ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label
                                                                    : formik.values.objective === 'OUTCOME_APP_PROMOTION'
                                                                    ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label
                                                                    : formik.values.objective === 'OUTCOME_AWARENESS'
                                                                    ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label
                                                                    : formik.values.objective === 'OUTCOME_ENGAGEMENT'
                                                                    ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                    : formik.values.objective === 'OUTCOME_TRAFFIC'
                                                                    ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                    : "Select a bid strategy")
                                                                : "Select a bid strategy"}
                                                            </Text>
                                                            <ChevronDownIcon boxSize='5' />
                                                            </Flex>
                                                        </MenuButton>
                                                        </Tooltip>

                                                        {isBudgetChanged ? null : (
                                                            <MenuList zIndex={20}>
                                                                {(formik.values.objective === 'OUTCOME_SALES' ? bidStrategiesForSales : bidStrategiesForLeads).map((option) => {
                                                                    const isBidCap = option?.value === 'LOWEST_COST_WITH_BID_CAP';
                                                                    const isCostCap = option?.value === 'COST_CAP';
                                                                    const isQualityLead = selectedCampaign && selectedCampaign?.adsets[0]?.optimization_goal === 'QUALITY_LEAD';

                                                                    // Skip rendering COST_CAP if optimization_goal is QUALITY_LEAD
                                                                    if (isCostCap && isQualityLead) {
                                                                        return null;
                                                                    }

                                                                    const MenuItemContent = (
                                                                        <Box
                                                                            display="flex"
                                                                            justifyContent="space-between"
                                                                            width="100%"
                                                                            alignItems="center"
                                                                            py={2}
                                                                            px={2}
                                                                        >
                                                                            <Box>
                                                                                <Text fontWeight="medium" fontSize="12px">{option.label}</Text>
                                                                                <Text fontSize="12px">{option.description}</Text>
                                                                            </Box>
                                                                            {isBidCap && selectedCampaign?.bid_strategy !== 'LOWEST_COST_WITH_BID_CAP' && (
                                                                                <ChevronRightIcon boxSize='5' />
                                                                            )}
                                                                        </Box>
                                                                    );

                                                                    return (isBidCap && selectedCampaign?.bid_strategy !== 'LOWEST_COST_WITH_BID_CAP') ? (
                                                                        <Popover trigger="hover" placement="right-start" closeOnBlur={false} key={option.value}>
                                                                            <PopoverTrigger>
                                                                                <Box width="100%">
                                                                                    <MenuItem
                                                                                        _hover={{ bg: 'gray.50' }}
                                                                                        p={0}
                                                                                    >
                                                                                        {MenuItemContent}
                                                                                    </MenuItem>
                                                                                </Box>
                                                                            </PopoverTrigger>
                                                                            <PopoverContent
                                                                                cursor='pointer'
                                                                                width="auto"
                                                                                onMouseEnter={(e) => e.stopPropagation()}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    formik.setFieldValue('bid_strategy', option.value);
                                                                                    onBidModalOpen();
                                                                                }}
                                                                            >
                                                                                <PopoverArrow />
                                                                                <PopoverBody>
                                                                                    <Text fontWeight="semibold" fontSize='14px'>Bid cap</Text>
                                                                                    <Text fontSize="12px">
                                                                                        Set the highest that you want to bid in any auction.
                                                                                    </Text>
                                                                                </PopoverBody>
                                                                            </PopoverContent>
                                                                        </Popover>
                                                                    ) : (
                                                                        <MenuItem
                                                                            key={option.value}
                                                                            onClick={() => {
                                                                                formik.setFieldValue('bid_strategy', option.value);
                                                                                if (
                                                                                    selectedCampaign?.bid_strategy === 'LOWEST_COST_WITHOUT_CAP' &&
                                                                                    option.value === 'LOWEST_COST_WITHOUT_CAP'
                                                                                ) {
                                                                                    return;
                                                                                } else if (selectedCampaign?.bid_strategy !== option.value && option.value !== 'LOWEST_COST_WITHOUT_CAP') {
                                                                                    onBidModalOpen();
                                                                                }
                                                                            }}
                                                                            _hover={{ bg: 'gray.50' }}
                                                                            py={2}
                                                                            flexDirection="column"
                                                                            alignItems="flex-start"
                                                                        >
                                                                            <Text fontWeight="medium" fontSize="12px">{option.label}</Text>
                                                                            <Text fontSize="12px">{option.description}</Text>
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </MenuList>
                                                        )}
                                                    </Menu>
                                                </Flex>

                                                <Flex alignItems={'center'}>
                                                    <Text fontSize={'10px'}>Not all bid strategies are available for all campaigns.</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="12px" p={'5px'}>
                                                                <Text >
                                                                    The highest-volume bid strategy is only available if all ad sets are using the same delivery optimisation.<br></br>
                                                                    Cost per result goal is only available for some delivery optimisations.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                    </Tooltip>
                                                </Flex>
                                            </Box>
                                        </Flex>

                                        <Divider />
                                        <Button justifyContent={'flex-start'} onClick={() => setIsSchedulingExpanded(!isSchedulingExpanded)} variant="link" rightIcon={isSchedulingExpanded ? <ChevronUpIcon color={buttonTextColor} /> : <ChevronDownIcon color={buttonTextColor} />}>
                                            <Text fontSize={'14px'} color={buttonTextColor}>{isSchedulingExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                        </Button> 

                                        {/* Collapsible content */}
                                        <Collapse in={isSchedulingExpanded}> 
                                            <Flex direction={'column'} gap={4}>
                                                    <Flex direction={'column'} gap={2}>
                                                        <Flex direction={'column'}>
                                                        <Text fontWeight="bold">Budget scheduling</Text>
                                                        <Text fontSize={'12px'}>Increase your budget during specific days or times.</Text>
                                                        </Flex>
                                                        <Flex>
                                                            <Tooltip
                                                                borderRadius={5}
                                                                p={2}
                                                                ml={20}
                                                                placement='top'
                                                                fontSize={'12px'}
                                                                label="Your high-demand periods are turned off and will not run until you enable budget scheduling."
                                                            >
                                                                <span>
                                                                    <Checkbox
                                                                        colorScheme={"brand"}
                                                                        name="is_budget_schedule_enabled"
                                                                        size={'md'}
                                                                        fontSize={'12px'}
                                                                        isChecked={formik?.values.is_budget_schedule_enabled}
                                                                        onChange={formik?.handleChange}
                                                                        isDisabled={formik?.values.budget !== 'daily_budget'}
                                                                    >
                                                                        <Text fontSize='12px'>Schedule budget increases</Text>
                                                                    </Checkbox>
                                                                </span>
                                                            </Tooltip>
                                                        </Flex>
                                                        {formik?.values?.is_budget_schedule_enabled && formik?.values?.budget === 'daily_budget' ? 
                                                            <UpdateBudgetScheduleSpecs
                                                                specs={formik?.values?.budget_schedule_specs}
                                                                setFieldValue={formik?.setFieldValue}
                                                                currency={adsAccountData?.currency}
                                                                onRemovePeriod={(id) => handleRemovePeriod(id)}
                                                                deletingIndex={deletingIndex}
                                                                onPeriodChanged={(period) => setLastChangedPeriod(period)} 
                                                            />
                                                        :null}
                                                    </Flex> 
                                                    <Flex direction='column' gap={1}>
                                                        <Flex alignItems={'center'}>
                                                            <Text fontWeight="bold">Ad scheduling</Text>
                                                            <Tooltip
                                                                borderRadius={5}
                                                                label={
                                                                    <Box fontSize="sm" p={'5px'}>
                                                                        <Text >
                                                                            You can schedule your ads to deliver on specific hours and days of the week based on the viewer's or your ad account's time zone.
                                                                        </Text>
                                                                    </Box>
                                                                }
                                                                fontSize="md"
                                                            >
                                                                <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                            </Tooltip>
                                                        </Flex>
                                                        <Text fontSize='12px'>You'll set the specific schedule within each ad set.</Text>
                                                        <Flex>
                                                            <Checkbox
                                                                size={'md'}
                                                                colorScheme='brand'
                                                                isChecked={formik.values.isAdScheduled}
                                                                onChange={() => formik.setFieldValue('isAdScheduled', !formik.values.isAdScheduled)}
                                                                isDisabled
                                                            >
                                                                <Flex direction='column'>
                                                                    <Text fontSize='12px'>Run ads on a schedule</Text>
                                                                    {formik.values.budget !== 'lifetime_budget' ?
                                                                        <Text fontSize='10px'>Ad scheduling is only available when you use lifetime budget.</Text>
                                                                        : null}
                                                                </Flex>
                                                            </Checkbox>
                                                        </Flex>
                                                    </Flex>

                                                    {formik.values.bid_strategy === 'LOWEST_COST_WITH_BID_CAP' ? 
                                                        <Flex direction="column" gap={2}>
                                                            <Flex align={'center'} mt={'10px'}>
                                                                <Text fontWeight="bold">Delivery type</Text>
                                                                <Tooltip
                                                                    borderRadius={5}
                                                                    label={
                                                                        <Flex direction={'column'} fontSize="12px" p={'5px'}>
                                                                            <Text>Standard delivery uses pacing to control your spend.</Text>
                                                                            <Text>Pacing keeps you from spending your budget too quickly and is the recommended option for most advertisers.</Text>
                                                                            <Text>Accelerated delivery can be useful for time-sensitive promotions. You need to set a bid cap to use accelerated delivery.</Text>
                                                                        </Flex>
                                                                    }
                                                                    fontSize="md"
                                                                >
                                                                    <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                                </Tooltip>
                                                            </Flex>
                                                            {/* Added Checkbox for Accelerated delivery */}
                                                            <Flex gap={2}>
                                                                <Checkbox
                                                                    colorScheme={"brand"}
                                                                    name="accelerated"
                                                                    size={'md'}
                                                                    isChecked={formik.values.accelerated}
                                                                    onChange={formik.handleChange}
                                                                />
                                                                <Flex direction="column">
                                                                    <Text fontSize="14px" fontWeight="bold">Accelerated</Text>
                                                                    <Text fontSize="12px">Spend your budget and get results as quickly as possible</Text>
                                                                </Flex>
                                                            </Flex>
                                                        </Flex>
                                                    :null}
                                            </Flex>
                                        </Collapse>
                                    </>
                                ) : (null)}
                            </Flex>
                        </Box>

                        {/* Submit Button in a separate Flex container */}
                        <Flex mt={4}>
                            <Button
                                colorScheme="brand"
                                isLoading={isCampaignUpdating}
                                isDisabled={!formik.isValid}
                                type="submit"
                                size={'sm'}
                                mb={'10px'}
                            >
                                Save
                            </Button>
                        </Flex>
                    </form>
                    :
                    <Flex justify="center" align="center" height="70vh">
                
                    </Flex>
                }
            </>
        }
    </Box>
  )
}

export default UpdateCampaignForm
